/* Formline store-locator widget — Leaflet + OpenStreetMap.
 *
 * Design notes:
 *   - Fetches the FULL stockist list ONCE per section on load. Lists
 *     are ≤ a couple hundred rows in practice; keeping everything in
 *     memory means search, distance sort, and marker interactions all
 *     run without further server round-trips.
 *   - Postcode search + "use my location" geocoding go through
 *     Nominatim (OSM's free geocoder). Kept polite per their usage
 *     policy: one request per user gesture, no automated bulk calls.
 *   - Zero third-party auth. No API keys. No billing. Drop-in.
 *   - Base map is OpenStreetMap standard tiles with a CSS greyscale
 *     filter applied via `.is-grayscale` on the map element — mimics
 *     the muted CARTO Positron look without needing a CARTO key.
 */
(function () {
  'use strict';

  // Each section on the page mounts independently — support multiple
  // instances just in case (Rick might drop the section on a landing
  // page too).
  document.querySelectorAll('.fl-locator').forEach(function (root) {
    initLocator(root);
  });

  function initLocator(root) {
    var api    = (root.dataset.api    || '').replace(/\/+$/, '');
    var list   =  root.dataset.list   || 'formline-stockists';
    var market =  root.dataset.market || '';
    var brand  =  root.dataset.brand  || 'Formline';

    var qEl        = root.querySelector('.fl-locator-q');
    var hereBtn    = root.querySelector('.fl-locator-here');
    var statusEl   = root.querySelector('.fl-locator-status');
    var listEl     = root.querySelector('.fl-locator-list');
    var mapEl      = root.querySelector('.fl-locator-map');

    var allStockists = [];   // authoritative list from the API
    var displayed    = [];   // current filtered/sorted view
    var markers      = [];   // Leaflet markers, index-aligned with `displayed`
    var map          = null;
    var cluster      = null;
    var activeIdx    = -1;

    // ---- boot ---------------------------------------------------
    // Wait for Leaflet's <script defer> to actually finish loading.
    ready(function () {
      buildMap();
      fetchAll();
      qEl.addEventListener('input', debounce(onSearch, 240));
      qEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); onSearch(); }
      });
      hereBtn.addEventListener('click', onHere);
    });

    function ready(fn) {
      if (window.L && window.L.markerClusterGroup) return fn();
      var t = setInterval(function () {
        if (window.L && window.L.markerClusterGroup) {
          clearInterval(t); fn();
        }
      }, 60);
      // Give up gracefully after 8s
      setTimeout(function () { clearInterval(t); }, 8000);
    }

    // ---- map ----------------------------------------------------
    function buildMap() {
      // Perth-ish default; recalibrated to fit results on first load.
      map = L.map(mapEl, { scrollWheelZoom: false }).setView(
        [-25.5, 134.5], 4);
      // Turn wheel zoom on after user click, off on blur — standard
      // "don't hijack scroll" behaviour.
      map.on('focus',  function () { map.scrollWheelZoom.enable(); });
      map.on('blur',   function () { map.scrollWheelZoom.disable(); });
      // OpenStreetMap standard tiles — no key, no cap, visible
      // attribution only. Muted CARTO-Positron-alike look comes from
      // the greyscale CSS filter in formline-locator.css.
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, subdomains: 'abc',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
      }).addTo(map);
      cluster = L.markerClusterGroup({
        spiderfyOnMaxZoom:   true,
        showCoverageOnHover: false,
        disableClusteringAtZoom: 12,
      });
      map.addLayer(cluster);
    }

    // ---- data ---------------------------------------------------
    function fetchAll() {
      status('Loading stockists…');
      var url = api + '?limit=500&list=' + encodeURIComponent(list) +
                (market ? '&market=' + encodeURIComponent(market) : '');
      fetch(url, { credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          allStockists = (d && d.stockists) || [];
          displayed = allStockists.slice();
          render();
          // Clear the "Loading…" status silently on success; the
          // list panel is self-evident. Only surface state on
          // errors and after a user-initiated search.
          if (!allStockists.length) {
            status('No stockists in this list yet.', 'error');
          } else {
            status('');
          }
        })
        .catch(function () {
          status('Couldn’t load stockists — please refresh.', 'error');
        });
    }

    // ---- search -------------------------------------------------
    function onSearch() {
      var q = (qEl.value || '').trim();
      if (!q) {
        displayed = allStockists.slice()
          .sort(defaultOrder);
        stripDistances();
        render(); fitToMarkers();
        status('');
        return;
      }
      // Fast path: token match against name/city/address/postcode. If
      // there are any matches, show them.
      var tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
      var textMatches = allStockists.filter(function (s) {
        var hay = [
          s.name, s.address, s.city, s.state, s.postcode, s.country
        ].join(' ').toLowerCase();
        return tokens.every(function (t) { return hay.indexOf(t) !== -1; });
      });
      if (textMatches.length) {
        displayed = textMatches; stripDistances();
        render(); fitToMarkers();
        status(displayed.length + ' match' +
               (displayed.length === 1 ? '' : 'es') + '.');
        return;
      }
      // Fall back: treat the query as a postcode / locality and
      // geocode it, then sort by distance.
      status('Locating "' + q + '"…');
      geocode(q).then(function (origin) {
        if (!origin) {
          status('No stockists match “' + q + '”.', 'error');
          displayed = []; render();
          return;
        }
        sortByDistance(origin);
        render(); fitToMarkers(origin);
        status(displayed.length + ' stockists ' +
               (displayed[0] && displayed[0].distance_km != null
                 ? '(nearest ' + displayed[0].distance_km.toFixed(1) + ' km)'
                 : ''));
      }).catch(function () {
        status('Search failed — try a different postcode.', 'error');
      });
    }

    function onHere() {
      if (!navigator.geolocation) {
        status('Your browser doesn’t support location sharing.', 'error');
        return;
      }
      hereBtn.disabled = true;
      status('Locating you…');
      navigator.geolocation.getCurrentPosition(function (pos) {
        hereBtn.disabled = false;
        sortByDistance({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        render(); fitToMarkers({ lat: pos.coords.latitude,
                                 lon: pos.coords.longitude });
        status(displayed.length + ' stockists — nearest ' +
               (displayed[0] && displayed[0].distance_km != null
                 ? displayed[0].distance_km.toFixed(1) + ' km away'
                 : 'at unknown distance'));
      }, function (err) {
        hereBtn.disabled = false;
        var msg = 'Couldn’t get your location.';
        if (err && err.code === err.PERMISSION_DENIED) {
          msg = 'Location permission denied — try typing a postcode.';
        }
        status(msg, 'error');
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 });
    }

    // ---- distance -----------------------------------------------
    function sortByDistance(origin) {
      var lat = origin.lat, lon = origin.lon;
      displayed = allStockists.slice().map(function (s) {
        if (s.lat == null || s.lon == null) {
          s.distance_km = null; return s;
        }
        s.distance_km = haversineKm(lat, lon, s.lat, s.lon);
        return s;
      }).sort(function (a, b) {
        if (a.distance_km == null && b.distance_km == null) return 0;
        if (a.distance_km == null) return 1;
        if (b.distance_km == null) return -1;
        return a.distance_km - b.distance_km;
      });
    }
    function stripDistances() {
      displayed.forEach(function (s) { s.distance_km = null; });
    }
    function haversineKm(la1, lo1, la2, lo2) {
      var R = 6371, toRad = Math.PI / 180;
      var dLat = (la2 - la1) * toRad, dLon = (lo2 - lo1) * toRad;
      var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(la1 * toRad) * Math.cos(la2 * toRad) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    // ---- geocoding via Nominatim --------------------------------
    // Kept respectful of their usage policy: one hit per user gesture,
    // country-scoped to AU + NZ where our stockists live.
    function geocode(q) {
      var url = 'https://nominatim.openstreetmap.org/search?format=json' +
                '&limit=1&countrycodes=au,nz&q=' + encodeURIComponent(q);
      return fetch(url, {
        headers: { 'Accept-Language': 'en' },
        credentials: 'omit',
      })
        .then(function (r) { return r.json(); })
        .then(function (rows) {
          if (!rows || !rows.length) return null;
          return { lat: parseFloat(rows[0].lat),
                   lon: parseFloat(rows[0].lon) };
        });
    }

    // ---- render -------------------------------------------------
    // Brand-coloured SVG pin — teardrop shape with a white inner
    // dot. Uses `currentColor` on the fill so a CSS colour rule on
    // the wrapper (via --fl-pin-color / --fl-brand) drives it. No
    // per-render allocation — Leaflet clones the divIcon internally.
    var PIN_SVG =
      '<svg viewBox="0 0 24 32" width="30" height="40" ' +
        'xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20' +
              'C24 5.4 18.6 0 12 0z" fill="currentColor"/>' +
        '<circle cx="12" cy="12" r="4.5" fill="#ffffff"/>' +
      '</svg>';

    var brandIcon = L.divIcon({
      className:  'fl-locator-pin',
      html:       PIN_SVG,
      iconSize:   [30, 40],
      iconAnchor: [15, 40],
      popupAnchor:[0, -34],
    });

    function render() {
      listEl.innerHTML = '';
      cluster.clearLayers();
      markers = [];
      if (!displayed.length) {
        var li = document.createElement('li');
        li.className = 'fl-locator-empty';
        li.textContent = 'No stockists match — try a wider search.';
        listEl.appendChild(li);
        return;
      }
      displayed.forEach(function (s, i) {
        listEl.appendChild(renderItem(s, i));
        if (s.lat != null && s.lon != null) {
          var m = L.marker([s.lat, s.lon], { icon: brandIcon })
                    .bindPopup(popupHtml(s));
          m.on('click', function () { selectItem(i, false); });
          markers.push(m);
          cluster.addLayer(m);
        } else {
          markers.push(null);
        }
      });
    }

    function renderItem(s, idx) {
      var li = document.createElement('li');
      li.className = 'fl-locator-item';
      li.setAttribute('role', 'button');
      li.setAttribute('tabindex', '0');
      li.dataset.idx = idx;

      var name = document.createElement('div');
      name.className = 'fl-locator-item-name';
      name.textContent = s.name || '(Unnamed stockist)';
      if (s.distance_km != null) {
        var pill = document.createElement('span');
        pill.className = 'fl-locator-item-dist';
        pill.textContent = s.distance_km < 10
          ? s.distance_km.toFixed(1) + ' km'
          : Math.round(s.distance_km) + ' km';
        name.appendChild(pill);
      }
      li.appendChild(name);

      var addr = document.createElement('div');
      addr.className = 'fl-locator-item-addr';
      addr.innerHTML = popupAddress(s);   // cascaded, matches popup
      li.appendChild(addr);

      // Primary action pills — phone / email / directions — visible
      // on both desktop and mobile.
      var links = renderLinks(s);
      if (links) li.appendChild(links);

      // "Full identity" block — website + socials — rendered into
      // every row but hidden by CSS on desktop; revealed below 800px
      // where the map disappears and the list has to carry the whole
      // information load.
      var extra = renderExtra(s);
      if (extra) li.appendChild(extra);

      li.addEventListener('click', function () { selectItem(idx, true); });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); selectItem(idx, true);
        }
      });
      return li;
    }

    function renderLinks(s) {
      // Order matches the popup exactly:
      //   Phone → Email → Website → Directions.
      // Website carries a modifier class so desktop can hide it (the
      // popup covers it there); on mobile all four stack vertically.
      var links = document.createElement('div');
      links.className = 'fl-locator-item-links';
      var any = false;
      if (s.phone) {
        var a = document.createElement('a');
        a.href = 'tel:' + s.phone.replace(/\s+/g, '');
        a.textContent = s.phone;
        a.addEventListener('click', function (e) { e.stopPropagation(); });
        links.appendChild(a); any = true;
      }
      if (s.email) {
        var em = document.createElement('a');
        em.href = 'mailto:' + s.email;
        em.textContent = 'Email';
        em.addEventListener('click', function (e) { e.stopPropagation(); });
        links.appendChild(em); any = true;
      }
      if (s.website) {
        var w = document.createElement('a');
        w.className = 'is-mobile-only';   // desktop hides this
        w.href = withHttp(s.website);
        w.target = '_blank'; w.rel = 'noopener';
        w.textContent = 'Website';
        w.addEventListener('click', function (e) { e.stopPropagation(); });
        links.appendChild(w); any = true;
      }
      if (s.lat != null && s.lon != null) {
        any = true;
        var q = encodeURIComponent(joinAddress(s) || (s.lat + ',' + s.lon));
        appendA(links, 'https://www.google.com/maps/search/?api=1&query=' + q,
                'Directions');
      }
      return any ? links : null;
    }

    function appendA(host, href, label) {
      var a = document.createElement('a');
      a.href = href; a.target = '_blank'; a.rel = 'noopener';
      a.textContent = label;
      a.addEventListener('click', function (e) { e.stopPropagation(); });
      host.appendChild(a);
    }

    // Mobile-only social icon row. Website is now part of
    // `renderLinks` so it sits above Directions in the stack;
    // socials are icon-only and separated by a small margin, no
    // divider line.
    function renderExtra(s) {
      var has = !!(s.facebook || s.instagram || s.linkedin || s.google);
      if (!has) return null;
      var row = document.createElement('div');
      row.className = 'fl-locator-item-socials';
      function social(href, icon, label) {
        var a = document.createElement('a');
        a.href = withHttp(href);
        a.target = '_blank'; a.rel = 'noopener';
        a.title = label; a.setAttribute('aria-label', label);
        a.innerHTML = icon;
        a.addEventListener('click', function (e) { e.stopPropagation(); });
        row.appendChild(a);
      }
      if (s.facebook)  social(s.facebook,  ICO.facebook,  'Facebook');
      if (s.instagram) social(s.instagram, ICO.instagram, 'Instagram');
      if (s.linkedin)  social(s.linkedin,  ICO.linkedin,  'LinkedIn');
      if (s.google)    social(s.google,    ICO.star,      'Google');
      return row;
    }

    // Feather-style outline icons (MIT). Fill = currentColor so the
    // pill's own `color: var(--fl-brand)` drives them.
    var ICO = (function () {
      function svg(inner) {
        return '<svg viewBox="0 0 24 24" width="12" height="12" ' +
               'fill="none" stroke="currentColor" stroke-width="2" ' +
               'stroke-linecap="round" stroke-linejoin="round" ' +
               'aria-hidden="true">' + inner + '</svg>';
      }
      return {
        phone: svg('<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>'),
        mail: svg('<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>'),
        directions: svg('<polygon points="3 11 22 2 13 21 11 13 3 11"/>'),
        globe: svg('<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>'),
        facebook: svg('<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>'),
        instagram: svg('<rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>'),
        linkedin: svg('<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>'),
        star: svg('<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>'),
      };
    })();

    function popupHtml(s) {
      // Portrait "identity card" — vertical stack of full-width action
      // pills (phone / email / directions / website) followed by a
      // tight row of icon-only social links. Matches the ProGus
      // popup shape.
      var name = escapeHtml(s.name || '(Unnamed stockist)');
      var out = ['<div class="fl-locator-popup">',
                 '<div class="fl-locator-popup-name">' + name + '</div>',
                 '<div class="fl-locator-popup-addr">' + popupAddress(s) +
                 '</div>'];

      // Action pills — full width, label + icon, vertically stacked.
      var actions = [];
      function action(href, icon, label, ext) {
        actions.push('<a href="' + escapeHtml(href) + '"' + (ext || '') +
                     '>' + icon + '<span>' + escapeHtml(label) +
                     '</span></a>');
      }
      if (s.phone) {
        action('tel:' + s.phone.replace(/\s+/g, ''), ICO.phone, s.phone);
      }
      if (s.email) {
        action('mailto:' + s.email, ICO.mail, s.email);
      }
      if (s.lat != null && s.lon != null) {
        var q = encodeURIComponent(joinAddress(s) || (s.lat + ',' + s.lon));
        action('https://www.google.com/maps/search/?api=1&query=' + q,
               ICO.directions, 'Directions',
               ' target="_blank" rel="noopener"');
      }
      if (s.website) {
        action(withHttp(s.website), ICO.globe, 'Website',
               ' target="_blank" rel="noopener"');
      }
      if (actions.length) {
        out.push('<div class="fl-locator-popup-actions">' +
                 actions.join('') + '</div>');
      }

      // Socials — icon-only, side-by-side, unlabelled. Sized as small
      // circular tap targets, no fill/border, colour = brand.
      var socials = [];
      function social(href, icon, label) {
        socials.push('<a href="' + escapeHtml(href) + '" ' +
                     'title="' + escapeHtml(label) + '" ' +
                     'aria-label="' + escapeHtml(label) + '" ' +
                     'target="_blank" rel="noopener">' + icon + '</a>');
      }
      if (s.facebook)  social(withHttp(s.facebook),  ICO.facebook,  'Facebook');
      if (s.instagram) social(withHttp(s.instagram), ICO.instagram, 'Instagram');
      if (s.linkedin)  social(withHttp(s.linkedin),  ICO.linkedin,  'LinkedIn');
      if (s.google)    social(withHttp(s.google),    ICO.star,      'Google');
      if (socials.length) {
        out.push('<div class="fl-locator-popup-socials">' +
                 socials.join('') + '</div>');
      }

      out.push('</div>');
      return out.join('');
    }

    // Some stockist rows store bare hostnames (e.g. "abellandscape.com.au")
    // — anchor tags treat those as relative paths and break. Prepend
    // https:// when there's no scheme; leave mailto:/tel:/existing
    // schemes alone.
    function withHttp(url) {
      var u = (url || '').trim();
      if (!u) return u;
      if (/^[a-z][a-z0-9+.\-]*:/i.test(u)) return u;
      return 'https://' + u;
    }

    function selectItem(idx, panMap) {
      // Highlight list row
      Array.prototype.forEach.call(
        listEl.querySelectorAll('.fl-locator-item'),
        function (el) { el.classList.remove('is-active'); });
      var row = listEl.querySelector('[data-idx="' + idx + '"]');
      if (row) {
        row.classList.add('is-active');
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      activeIdx = idx;
      var m = markers[idx];
      if (m) {
        if (panMap) {
          map.setView(m.getLatLng(), Math.max(map.getZoom(), 13),
                      { animate: true });
        }
        m.openPopup();
      }
    }

    function fitToMarkers(origin) {
      var pts = [];
      markers.forEach(function (m) { if (m) pts.push(m.getLatLng()); });
      if (origin && origin.lat != null && origin.lon != null) {
        pts.push(L.latLng(origin.lat, origin.lon));
      }
      if (!pts.length) return;
      if (pts.length === 1) {
        map.setView(pts[0], 12);
      } else {
        map.fitBounds(L.latLngBounds(pts), { padding: [28, 28], maxZoom: 13 });
      }
    }

    // ---- misc helpers ------------------------------------------
    function status(msg, kind) {
      statusEl.textContent = msg || '';
      statusEl.classList.toggle('is-error', kind === 'error');
    }
    function joinAddress(s) {
      return [s.address, s.city, s.state, s.postcode, s.country]
        .filter(Boolean).join(', ');
    }

    // Multi-line "cascaded" address for the popup. Line 1: street.
    // Line 2: city + state + postcode. Line 3: country.
    function popupAddress(s) {
      var line2 = [s.city, s.state, s.postcode].filter(Boolean).join(' ');
      var parts = [s.address, line2, s.country].filter(Boolean);
      return parts.map(escapeHtml).join('<br>');
    }
    function defaultOrder(a, b) {
      var pa = a.priority || 0, pb = b.priority || 0;
      if (pa !== pb) return pb - pa;
      return (a.name || '').localeCompare(b.name || '');
    }
    function debounce(fn, ms) {
      var t = null;
      return function () {
        var args = arguments, ctx = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, ms);
      };
    }
    function escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
  }
})();
